/* The states a read has other than "loaded": loading, failed, and a write that was refused.
   (Empty lives with `Panel`, whose `Empty` it renders.) */

/* Loading is a skeleton shaped like the commonest result - a register: a name, a bar-ish middle
   and a short figure per row - never a spinner (33-ui-build-spec, States). The bones are the global
   `.skeleton`; this only lays them out. The words "Loading…" are real text for assistive
   technology and for the tests that assert a screen is not stuck. */
.loading {
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 0;
  padding: 4px 0;
}

.loadingRow {
  display: grid;
  grid-template-columns: minmax(96px, 1.4fr) minmax(0, 3fr) 56px;
  align-items: center;
  column-gap: 12px;
  padding: 9px 0;
}

.loadingRow:nth-child(2n) .name {
  width: 62%;
}

.loadingRow:nth-child(3n) .name {
  width: 48%;
}

.name {
  width: 80%;
}

.mid {
  height: 6px;
  border-radius: var(--radius-pill);
}

.figure {
  width: 100%;
  justify-self: end;
}

/* The way out of a failed read. A real `<button>`, because a control that does something is not an
   anchor with no href, drawn as a quiet secondary control: a repair is not a decision, so it does
   not take the primary treatment (one primary per view). */
.retry {
  display: block;
  width: fit-content;
  margin-top: 8px;
  padding: 3px 10px;
  border: 1px solid var(--line-2);
  border-radius: var(--radius-ctl);
  background: var(--panel);
  font: inherit;
  font-size: var(--t-min);
  font-weight: 500;
  color: var(--ink-2);
  cursor: pointer;
}

.retry:hover {
  background: var(--panel-2);
  color: var(--ink);
}

.retry:active {
  background: var(--panel-3);
}

/* A failed read. It is a failure, so it is tinted and says so in words, but it is one block with
   one way out rather than a centred paragraph that reads like an empty list. */
.failed {
  max-width: 64ch;
  padding: 10px 12px;
  border: 1px solid var(--crit-line);
  border-radius: var(--radius-ctl);
  background: var(--crit-w);
}

.failedText {
  margin: 0;
  font-size: var(--t-dense);
  line-height: 1.5;
  color: var(--ink);
}

/* A **write** that was refused, which is a different thing from a read that failed: a read failing
   costs the reader a retry, and a write failing may or may not have happened. So it is louder than
   an empty state and quieter than a KPI, and the tone is carried by a word as well as a colour
   (design rule 8). */
.refused {
  border: 1px solid var(--crit-line);
  background: var(--crit-w);
  border-radius: var(--radius-ctl);
  padding: 8px 10px;
  margin: 8px 0 0;
  font-size: var(--t-dense);
  line-height: 1.5;
  color: var(--ink);
}

/* Unknown rather than failed. The warning palette rather than the critical one, because nothing
   here says the write was lost - it says nobody can tell yet. */
.unknown {
  border-color: var(--warn-line);
  background: var(--warn-w);
}

.refusedHead {
  display: block;
  font-weight: 600;
  margin-bottom: 2px;
}

.refusedNext {
  display: block;
  margin-top: 4px;
  color: var(--ink-2);
}
